/**
 * Staged uploads that begin when the guest taps Share.
 *
 * The take is held in memory during review. Share calls `commit()`, which starts the
 * transfer (or joins one already running), reports progress for the Share button, and
 * can be aborted via `abort()` if the guest cancels. Retake discards the stage before
 * any bytes are sent, so abandoned shots never land in S3.
 */

export const STAGED_IDLE = 'idle'
export const STAGED_UPLOADING = 'uploading'
export const STAGED_READY = 'ready'
export const STAGED_FAILED = 'failed'
export const STAGED_ABORTED = 'aborted'

export const UPLOAD_PHASE_SENDING = 'sending'
export const UPLOAD_PHASE_FINALIZING = 'finalizing'
export const UPLOAD_PHASE_COMPLETE = 'complete'

/** XHR `upload.onprogress` is bytes handed to the OS, not S3 accept. Cap below 100% until xhr.onload. */
export const UPLOAD_SEND_CAP = 0.95

export function isAbortError(error) {
  return error?.name === 'AbortError'
}

function abortError() {
  const error = new Error('Upload cancelled')
  error.name = 'AbortError'
  return error
}

/**
 * POST a presigned form to S3.
 *
 * XHR rather than fetch because it reports upload progress and cancels cleanly.
 * `upload.onprogress` is bytes accepted by the browser/OS stack, not S3's 201/204.
 * Mobile browsers often go quiet around ~50% until the socket drains, then sit
 * again after `upload.onload` while S3 writes the object. Cap the sending bar
 * at UPLOAD_SEND_CAP and emit `finalizing` so the UI can say "Finishing…"
 * instead of looking frozen.
 *
 * This is the current ANAPI contract (single presigned POST). Multipart Upload
 * would need new API fields; it is the AWS recommendation for very large objects
 * but is not available on this path today.
 */
export function postFormData({ url, formData, signal, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    const onAbort = () => xhr.abort()
    const report = (value, phase) => {
      if (typeof onProgress === 'function') onProgress(value, { phase })
    }

    xhr.open('POST', url)

    if (xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          report(Math.min(UPLOAD_SEND_CAP, event.loaded / event.total), UPLOAD_PHASE_SENDING)
        }
      }
      xhr.upload.onload = () => {
        report(UPLOAD_SEND_CAP, UPLOAD_PHASE_FINALIZING)
      }
    }

    xhr.onload = () => {
      signal?.removeEventListener?.('abort', onAbort)
      if (xhr.status >= 200 && xhr.status < 300) {
        report(1, UPLOAD_PHASE_COMPLETE)
        resolve({ status: xhr.status })
      } else {
        reject(new Error(`Upload failed (${xhr.status})`))
      }
    }
    xhr.onerror = () => {
      signal?.removeEventListener?.('abort', onAbort)
      reject(new Error('Upload failed. Check your connection and try again.'))
    }
    xhr.onabort = () => {
      signal?.removeEventListener?.('abort', onAbort)
      reject(abortError())
    }

    if (signal) {
      if (signal.aborted) {
        reject(abortError())
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
    }

    xhr.send(formData)
  })
}

export default class StagedUpload {
  /**
   * @param {{ upload: (ctx: { signal: AbortSignal, onProgress: (n: number) => void }) => Promise<any>,
   *           onChange?: (state: object) => void }} options
   */
  constructor({ upload, onChange = null }) {
    this._upload = upload
    this._onChange = onChange
    this._status = STAGED_IDLE
    this._promise = null
    this._controller = null
    this._result = null
    this._error = null
    this._progress = 0
    this._phase = null
  }

  get phase() {
    return this._phase
  }

  get status() {
    return this._status
  }

  get result() {
    return this._result
  }

  get error() {
    return this._error
  }

  get progress() {
    return this._progress
  }

  get isReady() {
    return this._status === STAGED_READY
  }

  get isInFlight() {
    return this._status === STAGED_UPLOADING
  }

  _emit() {
    this._onChange?.({
      status: this._status,
      progress: this._progress,
      phase: this._phase,
      error: this._error,
      result: this._result
    })
  }

  _setStatus(status) {
    this._status = status
    this._emit()
  }

  /**
   * Begin transferring. Safe to call repeatedly; a transfer already running or finished is
   * left alone. Failures are captured on the instance rather than thrown, so an optimistic
   * start never produces an unhandled rejection.
   */
  start() {
    if (this._status === STAGED_UPLOADING || this._status === STAGED_READY) {
      return this._promise
    }

    this._error = null
    this._progress = 0
    this._phase = UPLOAD_PHASE_SENDING
    this._controller = typeof AbortController === 'function' ? new AbortController() : null
    const signal = this._controller?.signal

    this._setStatus(STAGED_UPLOADING)

    this._promise = Promise.resolve()
      .then(() =>
        this._upload({
          signal,
          onProgress: (value, meta) => {
            const next = Number(value)
            if (!Number.isFinite(next)) return
            this._progress = Math.min(1, Math.max(0, next))
            if (meta?.phase) this._phase = meta.phase
            this._emit()
          }
        })
      )
      .then((result) => {
        if (signal?.aborted) throw abortError()
        this._result = result
        this._progress = 1
        this._phase = UPLOAD_PHASE_COMPLETE
        this._setStatus(STAGED_READY)
        return result
      })
      .catch((error) => {
        if (isAbortError(error) || signal?.aborted) {
          this._setStatus(STAGED_ABORTED)
        } else {
          this._error = error
          this._setStatus(STAGED_FAILED)
        }
        throw error
      })

    // The optimistic start has no caller awaiting it yet.
    this._promise.catch(() => {})

    return this._promise
  }

  /**
   * Resolve with the uploaded result, joining an in-flight transfer, returning immediately
   * if it already finished, and retrying if it previously failed or was cancelled.
   */
  commit() {
    if (this._status === STAGED_READY) return Promise.resolve(this._result)
    if (this._status === STAGED_UPLOADING) return this._promise
    return this.start()
  }

  /**
   * Cancel and discard. Anything already in flight never lands in S3.
   */
  abort() {
    if (this._controller && this._status === STAGED_UPLOADING) {
      this._controller.abort()
    }
    this._controller = null
    this._result = null
    this._progress = 0
    if (this._status !== STAGED_ABORTED) this._setStatus(STAGED_ABORTED)
  }
}
